import { Injectable } from '@angular/core';
import { AlarmGroup } from 'icreate/common/models/alarm-group';
import { getFooter, getPdfLayout } from './print-alarm-groups-utils';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AlarmGroupService } from '@service/alarm-group.service';
import { MatDialog } from '@angular/material/dialog';
import { PrintDialogComponent, PrintPageType } from '@components/print-dialog/print-dialog.component';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

@Injectable({ providedIn: 'root' })
export class PrintAlarmGroupService {
    constructor(
        private translateService: TranslateService,
        private alarmGroupService: AlarmGroupService,
        private dialog: MatDialog
    ) {}

    readonly NUM_ROWS_PAGE = 30;
    private DEFAULT_MARGIN = [2, 5, 5, 5];
    tableWidths = [30, 54, 230, 170];

    async triggerPrint(vessel, comp: boolean = false): Promise<boolean> {
        let loadingRef = null;
        let blobUrl: string | undefined;

        try {
            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.ALARM_GROUPS,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    showOptionsList: false,
                    notMain: comp,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;

            const { printType, version, iAPIShipyardlId } = shouldPrint;

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 0 },
            });

            loadingRef.componentInstance.data.progress = 15;

            const [pdfMake, pdfFonts, alarmGroups] = await Promise.all([
                import('pdfmake/build/pdfmake'),
                import('pdfmake/build/vfs_fonts'),
                this.alarmGroupService.getAllForPrint(vessel.id),
            ]);

            loadingRef.componentInstance.data.progress = 55;

            const pdfData = this.preparePdf(alarmGroups);

            loadingRef.componentInstance.data.progress = 85;

            const runtimeVfs = {
                ...pdfFonts.default?.pdfMake?.vfs,
            };

            const baseHref = window.location.origin + '/';

            const fonts = {
                MSGothic: {
                    normal: baseHref + 'assets/fonts/MSGothic.ttf',
                    bold: baseHref + 'assets/fonts/MSGothic.ttf',
                    italics: baseHref + 'assets/fonts/MSGothic.ttf',
                    bolditalics: baseHref + 'assets/fonts/MSGothic.ttf',
                },
            };

            const pdfDoc = pdfMake.default.createPdf(pdfData, null, fonts, runtimeVfs);
            const blob: Blob = await new Promise((resolve, reject) => {
                pdfDoc.getBlob(
                    (generatedBlob: Blob) => resolve(generatedBlob),
                    (err: any) => reject(err)
                );
            });

            if (loadingRef) {
                loadingRef.componentInstance.data.progress = 100;
                loadingRef.close();
                loadingRef = null;
            }

            const blobUrl = URL.createObjectURL(blob);
            const newWindow = window.open(blobUrl, '_blank');
            if (!newWindow) {
                throw new Error('Popup blocker prevented opening the new window.');
            }

            newWindow.onload = () => {
                try {
                    newWindow.print();
                } catch (error) {
                    console.error('Error printing PDF:', error);
                }
            };

            setTimeout(() => {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
            }, 300000);

            return true;
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        }
    }

    preparePdf(alarmGroups: AlarmGroup[]) {
        const pdfContent = this.generatePdfContent(alarmGroups);
        const pageFooter = { columns: getFooter(), margin: [290, 0, 0, 0] };

        return {
            pageSize: 'A4',
            footer: pageFooter,
            content: pdfContent,
            pageMargins: [66.693, 56.693, 58.347, 30],
            info: {
                title: 'Alarm-groups',
            },
            defaultStyle: {
                fontSize: 9,
                color: '#000000',
                font: 'MSGothic',
            },
            layout: {
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
                hLineWidth: () => 0.75,
                vLineWidth: () => 0.75,
                hLineColor: () => 'black',
                vLineColor: () => 'black',
            },
            background: function () {
                return [
                    {
                        canvas: [
                            { type: 'line', x1: 56.693, y1: 56.693, x2: 567.003, y2: 56.693, lineWidth: 1 },
                            { type: 'line', x1: 56.693, y1: 56.693, x2: 56.693, y2: 771.129, lineWidth: 1 },
                            { type: 'line', x1: 56.693, y1: 771.129, x2: 567.003, y2: 771.129, lineWidth: 1 },
                            { type: 'line', x1: 567.003, y1: 56.693, x2: 567.003, y2: 771.129, lineWidth: 1 },
                        ],
                    },
                ];
            },
        };
    }

    generatePdfContent(alarmGroups: AlarmGroup[]) {
        const allRows = [];
        let numberOfLines = 0;
        const groupNameRowSet: Set<number> = new Set();

        const targetGroups =
            !alarmGroups || alarmGroups.length === 0
                ? [{ id: '', groupNo: null, name: '', description: '', categories: [''], vesselId: '' } as AlarmGroup]
                : alarmGroups;

        for (const alarmGroup of targetGroups) {
            allRows.push(...this.generateTableBody(alarmGroup));
            groupNameRowSet.add(numberOfLines);
            const numberCat = Array.isArray(alarmGroup.categories)
                ? alarmGroup.categories.length
                : alarmGroup.categories
                  ? 1
                  : 1;
            numberOfLines += 1 + numberCat;
        }

        const pagesRows = this.calculatePages(groupNameRowSet, numberOfLines);
        const numPages = pagesRows.length;
        groupNameRowSet.add(numberOfLines);

        const pdfContent = [];
        for (let i = 0; i < numPages; i++) {
            const pageRows = pagesRows[i];
            const title = this.translateService.instant('alarm-group.page.print.title');
            const titleText = {
                text: title,
                font: 'MSGothic',
                fontSize: 12,
                alignment: 'center',
                margin: [5, 30, 0, 10],
            };
            const groupsRowsArray = allRows.slice(pageRows.first, pageRows.last + 1);

            let contentObject: { table: any; layout: any; pageBreak?: string } = {
                table: {
                    widths: this.tableWidths,
                    body: [...groupsRowsArray],
                    dontBreakRows: true,
                    margin: [10, 20, 10, 20],
                    heights: 20,
                },
                layout: getPdfLayout(groupNameRowSet, pageRows.first),
                pageBreak: 'after',
            };

            if (i === numPages - 1) delete contentObject.pageBreak;

            pdfContent.push(titleText);
            pdfContent.push(contentObject);
        }
        return pdfContent;
    }

    calculatePages(groupStarts: Set<number>, totalRows: number): { first: number; last: number }[] {
        const starts = [...groupStarts].sort((a, b) => a - b);

        const groupSizes = starts.map((start, i) => {
            const nextStart = i + 1 < starts.length ? starts[i + 1] : totalRows;
            return nextStart - start;
        });

        const pages = [];
        let rowsOnCurrentPage = 0;
        let pageFirstRow = 0;
        let currentRow = 0;

        for (const size of groupSizes) {
            if (rowsOnCurrentPage !== 0 && rowsOnCurrentPage + size > this.NUM_ROWS_PAGE) {
                pages.push({ first: pageFirstRow, last: currentRow - 1 });
                pageFirstRow = currentRow;
                rowsOnCurrentPage = 0;
            }
            rowsOnCurrentPage += size;
            currentRow += size;
        }

        if (rowsOnCurrentPage > 0) {
            pages.push({ first: pageFirstRow, last: currentRow - 1 });
        }

        return pages;
    }

    generateTableBody(alarmGroup: AlarmGroup) {
        const categories: string[] = Array.isArray(alarmGroup.categories)
            ? alarmGroup.categories.map((c: any) => c.category || '').sort((a, b) => a.localeCompare(b))
            : alarmGroup.categories
              ? [alarmGroup.categories]
              : [''];

        const nameText = this.translateService.instant('alarm-group.page.table.header.name');
        const categoryText = this.translateService.instant('alarm-group.page.table.header.category');

        const firstRow = [
            {
                rowSpan: categories.length + 1,
                text: alarmGroup.groupNo,
                font: 'MSGothic',
                margin: this.DEFAULT_MARGIN,
                noWrap: true,
                fillColor: '#d8d8d8',
                alignment: 'center',
            },
            {
                text: nameText,
                margin: this.DEFAULT_MARGIN,
                font: 'MSGothic',
                fillColor: '#d8d8d8',
                verticalAlignment: 'middle',
            },
            {
                text: alarmGroup.name.slice(0, 28),
                font: 'MSGothic',
                margin: this.DEFAULT_MARGIN,
                noWrap: false,
            },
            {
                text: alarmGroup.description,
                font: 'MSGothic',
                margin: this.DEFAULT_MARGIN,
                noWrap: false,
            },
        ];

        const rows: any = [firstRow];

        for (let i = 0; i < categories.length; i++) {
            const categoryName = categories[i];
            const categoriesRow = [
                '',
                {
                    rowSpan: categories.length,
                    text: categoryText,
                    margin: this.DEFAULT_MARGIN,
                    font: 'MSGothic',
                    fillColor: '#d8d8d8',
                },
                {
                    text: categoryName,
                    colSpan: 2,
                    font: 'MSGothic',
                    margin: this.DEFAULT_MARGIN,
                    noWrap: true,
                },
                '',
            ];
            if (i > 0) categoriesRow[1] = '';

            rows.push(categoriesRow);
        }

        return rows;
    }

    getTableHeader() {
        const numText = this.translateService.instant('alarm-group.page.table.header.num');
        const nameText = this.translateService.instant('alarm-group.page.table.header.name.long');
        const categoryText = this.translateService.instant('alarm-group.page.table.header.category');
        const descriptionText = this.translateService.instant('alarm-group.page.table.header.description');

        const rowHeader = [
            [
                {
                    rowSpan: 2,
                    text: numText,
                    alignment: 'center',
                    fillColor: '#d8d8d8',
                    verticalAlignment: 'middle',
                    margin: this.DEFAULT_MARGIN,
                },
                { text: nameText, margin: this.DEFAULT_MARGIN, fillColor: '#d8d8d8' },
                {
                    text: descriptionText,
                    margin: this.DEFAULT_MARGIN,
                    fillColor: '#d8d8d8',
                },
            ],
            [
                '',
                {
                    text: categoryText,
                    margin: this.DEFAULT_MARGIN,
                    colSpan: 2,
                    fillColor: '#d8d8d8',
                },
                '',
            ],
        ];

        return rowHeader;
    }

    getColumn1(alarmGroup: AlarmGroup) {
        return {
            text: alarmGroup.groupNo,
            font: 'MSGothic',
            margin: this.DEFAULT_MARGIN,
            noWrap: true,
        };
    }

    getColumn2(alarmGroup: AlarmGroup) {
        return {
            text: alarmGroup.name || '',
            font: 'MSGothic',
            margin: this.DEFAULT_MARGIN,
            noWrap: true,
        };
    }

    getColumn3(alarmGroup) {
        const categories = Array.isArray(alarmGroup.categories)
            ? alarmGroup.categories.map(c => c.category || '').join(', ')
            : alarmGroup.categories || '';
        return {
            text: categories,
            font: 'MSGothic',
            margin: this.DEFAULT_MARGIN,
            noWrap: true,
        };
    }
}
